import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { intervalSelector, currentMetricsMetaSelector } from '@store/common/selectors';
import useAnnotations from '@components/annotations/hooks/useAnnotations';
import useAnnotateBrush from '@components/annotations/hooks/useAnnotateBrush';
import { chartOption } from '../../utils/chartSchema';
import { CHART_TYPE } from '../utils';
import useZoom from '../useZoom';
import useTooltipSync from '../useTooltipSync';
import useSelect from '../useSelect';
import { TOOLS } from '../../control-panel/Toolbar';
import useMetricFilterSeries from '../useMetricFilterSeries';
import useReset from '../useReset';
import useManualAlerts from '../alerts/useManualAlerts';
import useAIAlerts from '../alerts/useAIAlerts';
import useDodSeries from '../useDodSeries';
import useCursor from '../useCursor';
import { logAxisAtom, unitAtom } from '../../atoms';
import useMetricSeries from '../useMetricSeries';
import { DATA_TAGS } from '../useData';
import useCompareSeries from '../useCompareSeries';
import useToggleSeries from '../useToggleSeries';
import {
  getInvertedPercentageMetricByKey,
  getMetricByUnit,
} from '@src/utils/metrics/metricHelpers';

function useSecondaryToolbox({ metric, activeTool, appliedFilter, naMode, diagMode }) {
  const [chartMode, setChartMode] = useState(CHART_TYPE.LINE);
  const [instance, setInstance] = useState(null);
  const unit = useAtomValue(unitAtom);
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const invertedMetric = getInvertedPercentageMetricByKey({
    metricKey: metric,
    defaultValue: metric,
  });
  const percentageMetric = getMetricByUnit('%', metric);
  const chartMetric = getMetricByUnit(unit === '%' ? '%' : '#', metric);
  const metricChartOption = chartOption({
    metric,
    allMetrics: currentMetricsMeta,
    appendTo: () => document.getElementsByTagName('body')[0],
  });
  const logAxis = useAtomValue(logAxisAtom);

  // chart instance with init option
  const chartInstanceRefCb: any = useCallback((instance) => {
    setInstance(instance);
    instance.setOption(metricChartOption);
  }, []);

  useMetricSeries({
    metric: chartMetric,
    instance,
    enabled: true,
    dataTag: DATA_TAGS.secondary,
    barChart: logAxis,
    logAxis,
  });

  // enable/disable cursor control for burshing action on chart
  useCursor({
    instance,
    activeTool,
    additionalTriggerDeps: [chartMode],
  });

  // double click to undo zoom/select
  useReset({ instance, enabled: true });

  // handle zoom interaction
  useZoom({
    instance,
    chartMode,
    enabled: [TOOLS.zoom, TOOLS.zoomToSelect].includes(activeTool),
  });

  // tooltip sync between charts
  useTooltipSync({ instance, tag: metric, enabled: !naMode && !diagMode });

  // handle select interaction
  useSelect({
    instance,
    chartMode,
    enabled: [TOOLS.select, TOOLS.zoomToSelect].includes(activeTool),
    metric,
  });

  // handle drawing of annotation
  useAnnotateBrush({
    instance,
    appliedFilter,
    metricKey: metric,
    enabled: activeTool === TOOLS.annotate,
  });

  // render annotation markers
  useAnnotations({
    metric: percentageMetric,
    instance,
    enabled: [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode),
    intervalSelector,
  });

  // metric filter series
  useMetricFilterSeries({
    metric: chartMetric,
    instance,
    additionalTriggerDeps: [chartMode],
    dataTag: DATA_TAGS.secondaryMetricFilter,
    barChart: logAxis,
  });

  // manual alert event markers
  useManualAlerts({
    metric: invertedMetric,
    instance,
    enabled: [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode),
    additionalTriggerDeps: [chartMode],
    drilldown: activeTool !== TOOLS.annotate,
  });

  // ai alert event markers
  useAIAlerts({
    metric: invertedMetric,
    instance,
    enabled: [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode),
    additionalTriggerDeps: [chartMode],
    drilldown: activeTool !== TOOLS.annotate,
  });

  // day over day timeseries
  useDodSeries({
    metric: chartMetric, // for unit toggle
    instance,
    enabled: [CHART_TYPE.LINE].includes(chartMode) && !logAxis,
    additionalTriggerDeps: [chartMode],
    dataTag: DATA_TAGS.secondaryDod,
    // barChart: logAxis,
  });

  useCompareSeries({
    metric: chartMetric, // for unit toggle
    instance,
    enabled: [CHART_TYPE.LINE].includes(chartMode) && !logAxis,
    additionalTriggerDeps: [chartMode],
    dataTag: DATA_TAGS.secondaryCompare,
    // barChart: logAxis,
  });

  // highlight series, used with compare
  useToggleSeries({ instance, enabled: true });

  return {
    chartMode,
    setChartMode,
    chartInstanceRefCb,
    instance,
  };
}

export default useSecondaryToolbox;
